import React, { useMemo, useState } from 'react';
import styles from '../../CoachChallenge/Table/Table.module.sass';
import { Avatars } from '../../../../../constants/const.js';
import { Loading } from '../../../../commonComponents/Loading/Loading.jsx';
import { DatePickerModal } from '../../../../ui/Modal/DatePicker';
import { FaCalendarAlt } from 'react-icons/fa';
import rStyles from '../index.module.sass';
import useTeamTrainingReport from '../../../../../hooks/useTeamTrainingReport.js';
import { timeConvert } from '../../../../../utils/utils.js';

export const playersHeaders = [
  { label: 'Name', accessor: 'childFirstName', sortable: false },
  { label: 'Week', accessor: 'week', sortable: true },
  { label: 'Month', accessor: 'month', sortable: true },
  { label: 'Year', accessor: 'year', sortable: true },
  { label: 'All Time', accessor: 'allTime', sortable: true },
];

function TrainingReportTable({ teamId }) {
  const {
    data: trainingReport,
    isLoading,
    selectedRange,
    setSelectedRange,
  } = useTeamTrainingReport(teamId);

  const data = trainingReport?.players ?? [];

  const [sortConfig, setSortConfig] = useState({ key: 'wins', direction: 'descending' });
  const [isOpen, setIsOpen] = useState(false);

  const sortAscDesc = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedPlayers = useMemo(() => {
    if (data) {
      const sortablePlayers = [...data]; // Create a copy of the player data
      if (sortConfig !== null) {
        sortablePlayers.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        });
      }
      return sortablePlayers;
    } else {
      return [];
    }
  }, [data, sortConfig]);

  if (isLoading) {
    return <Loading />;
  }

  if (!data) {
    return <span>No Data</span>;
  }

  return (
    <div
      style={{
        padding: '1rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <h2 className={styles.title}>Players</h2>
        <div className={styles.buttonActions}>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              alignItems: 'flex-end',
            }}
          >
            <button
              className={`${rStyles.button} ${rStyles.actionb}`}
              onClick={() => {
                setIsOpen(true);
              }}
            >
              <FaCalendarAlt style={{ marginRight: '10px' }} />
              Search by Calendar
            </button>
          </div>
        </div>
      </div>
      <table className={styles.player_data}>
        <thead>
          <tr className={styles.player_data__header}>
            {playersHeaders.map((header, i) => (
              <th
                scope="col"
                className={styles.player_data__header__item}
                key={i}
                onClick={() => header.sortable && sortAscDesc(header.accessor)}
                style={header.accessor === 'childFirstName' ? { width: '80%' } : {}}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: header.sortable ? 'pointer' : 'default',
                  }}
                >
                  <span
                    style={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {header.label}
                  </span>
                  {sortConfig.key === header.accessor ? (
                    <span
                      style={{
                        fontSize: '.75rem',
                        marginLeft: '.3rem',
                        marginTop: '5px',
                      }}
                    >
                      {sortConfig.direction === 'ascending' ? '▲' : '▼'}
                    </span>
                  ) : (
                    <>
                      {header.sortable && (
                        <span
                          style={{
                            fontSize: '.75rem',
                            marginLeft: '.3rem',
                            marginTop: '5px',
                          }}
                        >
                          ▼
                        </span>
                      )}
                    </>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={styles.player_data__main}>
          {sortedPlayers.map((data) => {
            const classSelector = data.isManager
              ? styles.cell_player_manager
              : styles.cell_player_name;
            const {
              childFirstName,
              childId,
              childLastName,
              image,
              picture,
              week,
              month,
              year,
              allTime,
            } = data;
            const fullName = childFirstName.concat(childLastName ? `${' ' + childLastName}` : ' ');
            return (
              <tr className={styles.row} key={childId}>
                <td className={classSelector}>
                  <img
                    src={image || Avatars[parseInt(picture) - 1]}
                    className={styles.cell_logo}
                    alt=""
                  />
                  <span>{fullName}</span>
                </td>
                <td className={styles.cell} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {week.count} | {timeConvert(week?.time)}
                </td>
                <td className={styles.cell} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {month.count} | {timeConvert(month?.time)}
                </td>
                <td className={styles.cell} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {year.count} | {timeConvert(year?.time)}
                </td>
                <td className={styles.cell} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {allTime.count} | {timeConvert(allTime?.time)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isOpen && (
        <DatePickerModal
          selectedRange={selectedRange}
          setSelectedRange={setSelectedRange}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

export default TrainingReportTable;
